import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Popover, PopoverBody, PopoverHeader } from "react-bootstrap";
import styled from "styled-components";
import { DateDisplay, ProfileDisplay } from "../../../components";
import { openClientVulnerabilityModal } from "../../../components/modals/client-factfind-vulnerability";
import { FixedCell } from "../../../components/tables/StandardTable";
import { TableLoadingRow } from "../../../components/tables/VirtualTable";
import { ThemedSpan } from "../../../components/utilities";
import { toNString } from "../../../helpers/guids";
import useOpenDocument from "../../../hooks/useOpenDocument";
import useOpenMasterAccount from "../../../hooks/useOpenMasterAccount";

const LargePopover = styled(Popover)`
    max-width: 500px;
`

/**
 * Wrapper component for the FixedCell that shows a Popover in the case of overflowing content
 */
const PopoverCell = ({ popoverHeader = null, popoverBody, children, ...rest }) => {

    return <OverlayTrigger overlay={(
        <LargePopover>
            {popoverHeader && <PopoverHeader>{popoverHeader}</PopoverHeader>}
            <PopoverBody>
                {popoverBody}
            </PopoverBody>
        </LargePopover>
    )}>
        <FixedCell {...rest}>
            {children}
        </FixedCell>
    </OverlayTrigger>
}

const AdviceRegisterTableRow = ({ data, index, style }) => {
    const { height } = style;

    const openMasterAccount = useOpenMasterAccount();
    const { openDocument, isFetching: isOpening } = useOpenDocument();

    if (data[index] == null) {
        return <TableLoadingRow style={{ height }} />
    }

    const {
        sentDate,
        compliantDate,
        reportName,
        reportTypeDescription,
        reportStatusDescription,
        masterAccountId,
        masterAccountReference,
        masterAccountName,
        documentId,
        authorId,
        author,
        adviserId,
        adviser,
        complianceUserId,
        complianceUser,
        rejectCount,
        compliantStatusDescription,
        adminStatusDescription,
        isVulnerable,
        replacement,
        reviewDate,
        serviceLevelAgreement,
        serviceLevelAgreementStatus,
        serviceLevelAgreementStatusDescription
    } = data[index];

    const handleOpenVulnerabilities = () => {
        openClientVulnerabilityModal(masterAccountId)
            .then(
                (proceed) => {
                    if (proceed) {
                        const sectionStorageKey = `${toNString(masterAccountId)}-fact-find-sections`;
                        const activeKeys = JSON.parse(sessionStorage.getItem(sectionStorageKey)) ?? [];
                        if (!activeKeys.includes("vulnerability")) {
                            activeKeys.push("vulnerability");
                            sessionStorage.setItem(sectionStorageKey, JSON.stringify(activeKeys));
                        }
                        openMasterAccount(masterAccountId, { suffix: "factfind" });
                    }
                }
            );
    }

    const handleOpenDocument = () => {
        if (isOpening)
            return;

        openDocument(documentId);
    }

    return <tr style={{ height }} className="align-middle">
        <FixedCell className="text-truncate">
            <ProfileDisplay userId={adviserId} userDisplayName={adviser} />
        </FixedCell>
        <PopoverCell className="text-truncate" popoverBody={`${masterAccountReference} ${masterAccountName}`}>
            <a href={`/client/${masterAccountId}`}>
                {masterAccountName}
            </a>
        </PopoverCell>
        <FixedCell className="text-truncate">
            <FontAwesomeIcon
                className="has-pointer"
                onClick={handleOpenVulnerabilities}
                size="sm"
                icon={isVulnerable ? "fa-flag" : ["far", "fa-flag"]}
                color={isVulnerable ? "red" : null}
            />
        </FixedCell>
        <PopoverCell className="text-truncate" popoverHeader={"Service Agreement"} popoverBody={<p className="m-0 lh-1">
            {serviceLevelAgreement}
            {serviceLevelAgreementStatus != null && <span className="fs-smallest"><br />{serviceLevelAgreementStatusDescription}</span>}
        </p>}>
            <p className="m-0 lh-1 text-truncate">
                <span>
                    {serviceLevelAgreement}
                    {serviceLevelAgreementStatus != null && <>
                        <ThemedSpan variant="muted" className="fs-smallest">
                            <br />
                            {serviceLevelAgreementStatusDescription}
                        </ThemedSpan>
                    </>}
                </span>
            </p>
        </PopoverCell>
        <FixedCell className="text-truncate">
            <DateDisplay>{reviewDate}</DateDisplay>
        </FixedCell>
        <PopoverCell className="text-truncate" popoverHeader={"Report Details"} popoverBody={<p className="m-0 lh-1">
            {reportName}<br />
            <span className="fs-smallest">
                {reportTypeDescription} - {reportStatusDescription}
                {replacement && <><br /><strong>Replacement</strong></>}
            </span>
        </p>}>
            <p className="m-0 lh-1 text-truncate">
                <span>
                    <button className="anchor" onClick={handleOpenDocument}>{reportName}</button>
                    <br />
                    <ThemedSpan variant="muted" className="fs-smallest">
                        {reportTypeDescription} - {reportStatusDescription}
                        {replacement && <><br /><strong>Replacement</strong></>}
                    </ThemedSpan>
                </span>
            </p>
        </PopoverCell>
        <FixedCell className="text-truncate">
            <ProfileDisplay userId={authorId} userDisplayName={author} />
        </FixedCell>
        <FixedCell className="text-truncate">
            <ProfileDisplay userId={complianceUserId} userDisplayName={complianceUser} />
        </FixedCell>
        <FixedCell className="text-truncate text-end">
            {rejectCount}
        </FixedCell>
        <FixedCell className="text-truncate">
            {compliantDate == null
                ? "Not Compliant"
                : <p className="m-0 lh-1 text-truncate">
                    <span>
                        {compliantStatusDescription}
                        <br />
                        <DateDisplay variant="muted" className="fs-smaller">
                            {compliantDate}
                        </DateDisplay>
                    </span>
                </p>}
        </FixedCell>
        <FixedCell className="text-truncate">
            {sentDate == null ? "Not Sent" : <DateDisplay>{sentDate}</DateDisplay>}
        </FixedCell>
        <FixedCell className="text-truncate">
            {adminStatusDescription}
        </FixedCell>
    </tr>
}

export default AdviceRegisterTableRow;